/**
 * Vorlagen fuer die Gewerkeseiten des SiGe-Handbuchs.
 *
 * Herkunft: `sige-plan/src/config/trade-templates.ts` der Quell-App. Dort
 * standen FUENF Vorlagen (Rohbau, Elektro, Sanitaer, Dachdeckung, Fassade);
 * hier stehen alle Gewerke der kanonischen Liste. Entscheidung Nr. 7 des
 * Orchestrators: fuenf Vorlagen decken kein reales Bauvorhaben ab, und ein
 * Gewerk ohne Vorlage bekommt eine leere Seite — was aussieht, als gaebe es
 * dort nichts zu beurteilen.
 *
 * ═══ WARUM NUR EIN TYP-IMPORT AUS `gewerkeKatalog.ts` ══════════════════════
 * `import type { GewerkId }` erzeugt KEINE Laufzeit-Abhaengigkeit (der Import
 * verschwindet beim Uebersetzen), verschafft aber genau die Kopplung, die man
 * hier haben will: `Record<GewerkId, GewerkVorlage>` laesst den Compiler
 * pruefen, dass JEDES Gewerk des Katalogs eine Vorlage hat und dass keine
 * Vorlage eine id erfindet. Waechst der Katalog um ein Gewerk, bricht der
 * Build hier — und das ist das gewuenschte Verhalten, denn sonst faellt erst
 * auf der Baustelle auf, dass die neue Gewerkeseite leer bleibt.
 *
 * Die BEZEICHNUNGEN stehen bewusst NICHT hier. Sie kommen zur Laufzeit aus
 * `gewerkBezeichnung()` des Katalogs (Aufruf in `handbuchLogic.ts`). Zwei
 * Listen von Anzeigetexten fuer dieselben Gewerke liefen unweigerlich
 * auseinander, und dann hiesse dasselbe Gewerk im Handbuch anders als in der
 * Koordinierung.
 *
 * ═══ WARUM BAUSTEINE STATT AUSGESCHRIEBENER LISTEN ═════════════════════════
 * „Schutzhelm EN 397, alle Taetigkeiten" stand in der Quell-App fuenfmal
 * woertlich da. Bei 24 Gewerken waeren es 24 Stellen, an denen eine geaenderte
 * Norm nachgezogen werden muesste — 23 davon wuerde jemand vergessen. PSA,
 * typische Gefaehrdungen und typische Nachweise liegen deshalb als BAUSTEINE
 * mit Code vor; die Vorlage nennt nur die Codes und ergaenzt, was wirklich nur
 * fuer sie gilt.
 *
 * Reine Daten und pure Funktionen — kein DOM, kein React, keine Supabase.
 */

import type { GewerkId } from '../koordinierung/gewerkeKatalog';
import type { PsaEintrag, RisikoStufe, TopTyp } from './types';

/**
 * Fassung dieses Katalogs.
 *
 * Wird ueber `aktuelleKatalogVersion()` in `handbuecher.katalog_version`
 * festgehalten. Woertlich die Begruendung zum Fragenkatalog der Planungsphase:
 * waechst der Katalog, aendert sich NICHT, was in einem abgeschlossenen
 * Projekt stand. Beim Erhoehen die Aenderung hier vermerken.
 *
 * Fassung 1: Erstbestueckung — die fuenf portierten Vorlagen plus 19 neue,
 * gefuehrt ueber PSA-, Gefaehrdungs- und Nachweisbausteine.
 */
export const VORLAGEN_VERSION = 1;

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE — PSA
// ═══════════════════════════════════════════════════════════════════════════

export type PsaCode =
    | 'helm' | 'schuhe' | 'warnkleidung'
    | 'handschuhe_mech' | 'handschuhe_chem' | 'handschuhe_schnitt'
    | 'gehoerschutz' | 'schutzbrille' | 'gesichtsschutz'
    | 'staubmaske_ffp2' | 'atemschutz_p3' | 'vollschutz'
    | 'auffanggurt' | 'isolierhandschuhe' | 'schweisserschild'
    | 'knieschutz' | 'schnittschutzhose' | 'chemikalienstiefel';

/**
 * Die Grundausstattung, die auf jeder Baustelle gilt.
 *
 * Steht als eigene Konstante und nicht in jeder Vorlage, weil sie auf jeder
 * Gewerkeseite erscheinen MUSS: eine Seite ohne Helm und Sicherheitsschuhe
 * liest sich, als waere dort keine PSA noetig.
 */
export const PSA_GRUNDAUSSTATTUNG: PsaCode[] = ['helm', 'schuhe'];

export const PSA_BAUSTEINE: Record<PsaCode, PsaEintrag> = {
    helm: { bezeichnung: 'Schutzhelm', norm: 'EN 397', taetigkeiten: 'Alle Tätigkeiten' },
    schuhe: { bezeichnung: 'Sicherheitsschuhe', norm: 'S3 (EN ISO 20345)', taetigkeiten: 'Alle Tätigkeiten' },
    warnkleidung: { bezeichnung: 'Warnkleidung', norm: 'EN ISO 20471 Klasse 2', taetigkeiten: 'Arbeiten im Bereich von Fahrzeug- und Geräteverkehr' },
    handschuhe_mech: { bezeichnung: 'Schutzhandschuhe (mechanisch)', norm: 'EN 388', taetigkeiten: 'Handhabung von Material und Werkzeug' },
    handschuhe_chem: { bezeichnung: 'Chemikalienschutzhandschuhe', norm: 'EN ISO 374', taetigkeiten: 'Umgang mit Gefahrstoffen, Frischbeton, Klebern' },
    handschuhe_schnitt: { bezeichnung: 'Schnittschutzhandschuhe', norm: 'EN 388 (Level D/E)', taetigkeiten: 'Blech-, Glas- und Messerarbeiten' },
    gehoerschutz: { bezeichnung: 'Gehörschutz', norm: 'EN 352', taetigkeiten: 'Arbeiten über 85 dB(A): Stemmen, Sägen, Rüttler, Bohren' },
    schutzbrille: { bezeichnung: 'Schutzbrille', norm: 'EN 166', taetigkeiten: 'Stemmen, Flexen, Bohren über Kopf' },
    gesichtsschutz: { bezeichnung: 'Gesichtsschutzschirm', norm: 'EN 166', taetigkeiten: 'Trennschleifen, Spritzgefahr' },
    staubmaske_ffp2: { bezeichnung: 'Partikelfiltrierende Halbmaske FFP2', norm: 'EN 149', taetigkeiten: 'Staubende Arbeiten ohne wirksame Absaugung' },
    atemschutz_p3: { bezeichnung: 'Atemschutz P3 / gebläseunterstützt', norm: 'EN 143 / EN 12941', taetigkeiten: 'Arbeiten mit krebserzeugenden Stäuben und Fasern' },
    vollschutz: { bezeichnung: 'Schutzanzug Typ 5/6 (Einweg)', norm: 'EN ISO 13982 / EN 13034', taetigkeiten: 'Schadstoffsanierung, Arbeiten im Schwarzbereich' },
    auffanggurt: { bezeichnung: 'Auffanggurt mit Verbindungsmittel', norm: 'EN 361 / EN 355', taetigkeiten: 'Arbeiten an Absturzkanten ohne Seitenschutz' },
    isolierhandschuhe: { bezeichnung: 'Isolierende Handschuhe', norm: 'EN 60903', taetigkeiten: 'Arbeiten an oder nahe elektrischen Anlagen' },
    schweisserschild: { bezeichnung: 'Schweißerschutzschild und -kleidung', norm: 'EN 175 / EN ISO 11611', taetigkeiten: 'Schweiß- und Schneidarbeiten' },
    knieschutz: { bezeichnung: 'Knieschutz', norm: 'EN 14404', taetigkeiten: 'Andauernde Arbeiten im Knien' },
    schnittschutzhose: { bezeichnung: 'Schnittschutzhose', norm: 'EN ISO 11393', taetigkeiten: 'Arbeiten mit der Motorsäge' },
    chemikalienstiefel: { bezeichnung: 'Chemikalienbeständige Stiefel', norm: 'EN 13832', taetigkeiten: 'Arbeiten in frischem Beton oder Estrich' },
};

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE — TYPISCHE EIGENGEFAEHRDUNGEN
// ═══════════════════════════════════════════════════════════════════════════
//
// EIGENGEFAEHRDUNG heisst: das Gewerk gefaehrdet SICH SELBST. Was ein Gewerk
// den ANDEREN antut, steht nicht hier, sondern im Paarungs- und Regelkatalog
// der Koordinierung (`GEWERK_REGELN`, `PAARUNGEN`). Die Trennung ist wichtig:
// das Handbuch fuellt damit die Gewerkeseite, die Koordinierung die Matrix,
// und keiner der beiden erfindet die Angaben des anderen.

export type GefaehrdungCode =
    | 'absturz_geruest' | 'absturz_dach' | 'absturz_kante' | 'absturz_oeffnung'
    | 'verschuettung' | 'kranlast' | 'herabfallend'
    | 'laerm' | 'vibration' | 'staub_quarz' | 'faserstaub'
    | 'hautkontakt_zement' | 'loesemittel' | 'schweissrauch'
    | 'stromschlag' | 'baustrom' | 'brand_heissarbeit'
    | 'stolpern' | 'sturz_glatt' | 'maschine_schnitt'
    | 'verbruehung' | 'ertrinken' | 'verkehr' | 'zwangshaltung' | 'handtransport'
    | 'enge_raeume' | 'einsturz';

export interface VorlageGefaehrdung {
    beschreibung: string;
    risiko: RisikoStufe;
    schutzmassnahme: string;
    /** Rangfolge: technisch vor organisatorisch vor personenbezogen. */
    top: TopTyp;
}

export const GEFAEHRDUNG_BAUSTEINE: Record<GefaehrdungCode, VorlageGefaehrdung> = {
    absturz_geruest: { beschreibung: 'Absturz vom Gerüst', risiko: 'hoch', schutzmassnahme: 'Dreiteiliger Seitenschutz, Belag vollflächig, Freigabekennzeichnung mit Lastklasse und Datum am Zugang; Prüfung vor jeder Benutzung.', top: 'T' },
    absturz_dach: { beschreibung: 'Absturz von der Dachfläche', risiko: 'hoch', schutzmassnahme: 'Fanggerüst oder Dachfangnetz an der Traufe, Absturzsicherung an Öffnungen; PSA gegen Absturz nur, wo Seitenschutz technisch nicht möglich ist.', top: 'T' },
    absturz_kante: { beschreibung: 'Absturz an ungesicherter Kante (Decke, Balkon, Baugrube)', risiko: 'hoch', schutzmassnahme: 'Seitenschutz ab 1,00 m Absturzhöhe, Kanten kennzeichnen; Anschlagpunkte vorab festlegen und dokumentieren.', top: 'T' },
    absturz_oeffnung: { beschreibung: 'Durchsturz durch Boden- und Schachtöffnungen', risiko: 'hoch', schutzmassnahme: 'Öffnungen tragfähig und unverschiebbar abdecken oder umwehren; Abdeckungen gegen Wegnehmen sichern und kennzeichnen.', top: 'T' },
    verschuettung: { beschreibung: 'Verschüttung in Baugrube oder Graben', risiko: 'hoch', schutzmassnahme: 'Verbau oder Böschung nach DIN 4124, Standsicherheitsnachweis; tägliche Sichtprüfung und Prüfung nach Regen und Frost.', top: 'T' },
    kranlast: { beschreibung: 'Aufenthalt im Gefahrbereich schwebender Lasten', risiko: 'hoch', schutzmassnahme: 'Lastwege festlegen und kennzeichnen, Anschlagmittel geprüft, Einweiser mit vereinbarten Zeichen; kein Aufenthalt unter der Last.', top: 'O' },
    herabfallend: { beschreibung: 'Herabfallende Bauteile, Werkzeuge und Material', risiko: 'hoch', schutzmassnahme: 'Gefahrbereich absperren, Schutzdach oder Fangnetz; Werkzeug gegen Herabfallen sichern, kein Abwerfen von Material.', top: 'T' },
    laerm: { beschreibung: 'Lärmbelastung über 85 dB(A)', risiko: 'mittel', schutzmassnahme: 'Lärmarme Geräte, Kapselung, räumliche und zeitliche Trennung lauter Arbeiten; Gehörschutz bereitstellen und tragen.', top: 'T' },
    vibration: { beschreibung: 'Hand-Arm-Vibration', risiko: 'mittel', schutzmassnahme: 'Vibrationsarme Geräte, Expositionszeiten begrenzen, Pausenregelung; arbeitsmedizinische Vorsorge anbieten.', top: 'T' },
    staub_quarz: { beschreibung: 'Quarzhaltiger Staub (A- und E-Staub)', risiko: 'hoch', schutzmassnahme: 'Absaugung direkt am Werkzeug oder Nassverfahren, Staubschutzwand; Atemschutz nur ergänzend, nicht als Ersatz.', top: 'T' },
    faserstaub: { beschreibung: 'Faserförmige Stäube (Asbest, KMF)', risiko: 'hoch', schutzmassnahme: 'Emissionsarme Verfahren nach TRGS 519 / TRGS 521, Schwarz-Weiß-Anlage, Unterdruckhaltung; Freimessung vor Freigabe.', top: 'T' },
    hautkontakt_zement: { beschreibung: 'Hautverätzung und Ekzem durch Frischbeton, Mörtel und Estrich', risiko: 'mittel', schutzmassnahme: 'Chromatarme Bindemittel, Hautschutzplan mit Schutz-, Reinigungs- und Pflegemittel; Schutzhandschuhe und geschlossene Kleidung.', top: 'T' },
    loesemittel: { beschreibung: 'Lösemittel- und Isocyanatbelastung (Kleber, Lacke, PU-Schaum)', risiko: 'mittel', schutzmassnahme: 'Lösemittelarme Produkte einsetzen, Räume während und nach der Arbeit lüften; Sicherheitsdatenblätter und Betriebsanweisung am Ort der Arbeit.', top: 'T' },
    schweissrauch: { beschreibung: 'Schweißrauche und Ozon', risiko: 'mittel', schutzmassnahme: 'Erfassung an der Entstehungsstelle, Lüftung; bei Chrom-Nickel-Werkstoffen zusätzlich Atemschutz.', top: 'T' },
    stromschlag: { beschreibung: 'Elektrischer Schlag bei Arbeiten an Anlagen', risiko: 'hoch', schutzmassnahme: 'Fünf Sicherheitsregeln einhalten, Freischaltung schriftlich bestätigen; Arbeiten nur durch Elektrofachkraft oder unter ihrer Leitung.', top: 'O' },
    baustrom: { beschreibung: 'Fehlerhafte Baustromversorgung, beschädigte Leitungen', risiko: 'mittel', schutzmassnahme: 'Speisung nur über Baustromverteiler mit RCD 30 mA, Leitungen erhöht führen; Prüfung nach DGUV V3 mit dokumentierten Fristen.', top: 'T' },
    brand_heissarbeit: { beschreibung: 'Brand- und Explosionsgefahr bei Heißarbeiten', risiko: 'hoch', schutzmassnahme: 'Erlaubnisschein, brennbare Stoffe entfernen oder abdecken, Löschmittel bereit; Brandwache während der Arbeit und Nachkontrolle.', top: 'O' },
    stolpern: { beschreibung: 'Stolpern und Stürzen über Leitungen, Material und Bauschutt', risiko: 'mittel', schutzmassnahme: 'Verkehrswege freihalten und beleuchten, Kabelbrücken, tägliche Ordnung im Arbeitsbereich.', top: 'T' },
    sturz_glatt: { beschreibung: 'Ausrutschen auf nassem, vereistem oder verschmutztem Untergrund', risiko: 'mittel', schutzmassnahme: 'Wege abstumpfen und räumen, Wasseraustritt sofort binden; rutschhemmendes Schuhwerk.', top: 'T' },
    maschine_schnitt: { beschreibung: 'Schnitt- und Fangverletzungen an Maschinen', risiko: 'mittel', schutzmassnahme: 'Schutzeinrichtungen benutzen und nicht überbrücken, Zuführhilfen; Unterweisung an der konkreten Maschine.', top: 'T' },
    verbruehung: { beschreibung: 'Verbrühung durch Heißwasser und Dampf', risiko: 'mittel', schutzmassnahme: 'Anlagen vor dem Öffnen entleeren und drucklos machen, Absperrarmaturen sichern und kennzeichnen.', top: 'T' },
    ertrinken: { beschreibung: 'Ertrinken bei Arbeiten am und über Wasser', risiko: 'hoch', schutzmassnahme: 'Absturzsicherung zum Wasser, Rettungsmittel und Rettungskette festlegen; Rettungsweste bei unvermeidbarer Gefahr.', top: 'T' },
    verkehr: { beschreibung: 'Angefahren werden durch Baustellen- oder öffentlichen Verkehr', risiko: 'hoch', schutzmassnahme: 'Verkehrssicherung nach RSA 21, Fußwege von Fahrwegen körperlich trennen; Rückfahrhilfen und Einweiser.', top: 'T' },
    zwangshaltung: { beschreibung: 'Zwangshaltung und einseitige Belastung', risiko: 'niedrig', schutzmassnahme: 'Arbeitshöhe anpassen, Hebehilfen einsetzen, Tätigkeitswechsel; Knieschutz bei Arbeiten im Knien.', top: 'T' },
    handtransport: { beschreibung: 'Belastung der Wirbelsäule beim manuellen Heben und Tragen', risiko: 'mittel', schutzmassnahme: 'Transporthilfen und Hebezeuge bereitstellen, Lasten teilen; Unterweisung zum Heben und Tragen.', top: 'T' },
    enge_raeume: { beschreibung: 'Sauerstoffmangel und Gefahrstoffe in engen Räumen und Schächten', risiko: 'hoch', schutzmassnahme: 'Befahrerlaubnis, Freimessung und Dauerüberwachung, Lüftung; Sicherungsposten außerhalb mit Rettungsmittel.', top: 'O' },
    einsturz: { beschreibung: 'Unkontrollierter Einsturz von Bauteilen', risiko: 'hoch', schutzmassnahme: 'Statische Beurteilung und Abbruchanweisung, Abbruchfolge einhalten, Restbauteile abstützen; Gefahrbereich absperren.', top: 'O' },
};

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE — TYPISCHE NACHWEISE
// ═══════════════════════════════════════════════════════════════════════════

export type NachweisCode =
    | 'gefaehrdungsbeurteilung' | 'unterweisung' | 'betriebsanweisung'
    | 'geruestfreigabe' | 'kranpruefung' | 'kranfuehrer' | 'anschlaeger'
    | 'psaga_pruefung' | 'leiter_geruest_pruefung' | 'elektro_pruefung'
    | 'elektrofachkraft' | 'schweisserpruefung' | 'erlaubnisschein'
    | 'sachkunde_trgs519' | 'entsorgungsnachweis' | 'statik_freigabe'
    | 'druckpruefung' | 'verkehrsrechtliche_anordnung' | 'ersthelfer'
    | 'vorsorge' | 'sicherheitsdatenblaetter' | 'maschinen_pruefbuch'
    | 'ausbau_abbruchanweisung' | 'freimessung' | 'inbetriebnahme';

export const NACHWEIS_BAUSTEINE: Record<NachweisCode, string> = {
    gefaehrdungsbeurteilung: 'Gefährdungsbeurteilung der ausführenden Firma',
    unterweisung: 'Unterweisungsnachweise der eingesetzten Beschäftigten',
    betriebsanweisung: 'Betriebsanweisungen für Gefahrstoffe und Maschinen',
    geruestfreigabe: 'Gerüstfreigabe und Prüfprotokoll',
    kranpruefung: 'Kranprüfung (BetrSichV / DGUV Vorschrift 52)',
    kranfuehrer: 'Befähigungsnachweis Kranführer',
    anschlaeger: 'Befähigungsnachweis Anschläger und Einweiser',
    psaga_pruefung: 'Prüfnachweis PSA gegen Absturz (jährlich)',
    leiter_geruest_pruefung: 'Prüfnachweis Leitern, Tritte und Fahrgerüste',
    elektro_pruefung: 'Prüfprotokoll ortsveränderlicher Betriebsmittel (DGUV V3)',
    elektrofachkraft: 'Nachweis Elektrofachkraft / Schaltberechtigung',
    schweisserpruefung: 'Schweißerprüfung und Verfahrensprüfung',
    erlaubnisschein: 'Erlaubnisschein für Heißarbeiten',
    sachkunde_trgs519: 'Sachkundenachweis nach TRGS 519 / TRGS 521',
    entsorgungsnachweis: 'Entsorgungsnachweis für gefährliche Abfälle',
    statik_freigabe: 'Statische Berechnung und Freigabe des Tragwerks',
    druckpruefung: 'Druck- und Dichtheitsprüfprotokoll',
    verkehrsrechtliche_anordnung: 'Verkehrsrechtliche Anordnung und Absicherungsplan',
    ersthelfer: 'Nachweis benannter Ersthelfer der Firma',
    vorsorge: 'Bescheinigung arbeitsmedizinischer Pflichtvorsorge',
    sicherheitsdatenblaetter: 'Sicherheitsdatenblätter der eingesetzten Gefahrstoffe',
    maschinen_pruefbuch: 'Prüfbuch der eingesetzten Arbeitsmittel',
    ausbau_abbruchanweisung: 'Abbruch- bzw. Rückbauanweisung mit Abbruchfolge',
    freimessung: 'Freimessprotokoll vor Freigabe des Bereichs',
    inbetriebnahme: 'Inbetriebnahme- und Abnahmeprotokoll der Anlage',
};

/**
 * Die drei Nachweise, die jede Firma auf jeder Baustelle schuldet.
 *
 * Getrennt gefuehrt, damit sie nicht 24-mal abgetippt werden — und damit man
 * sie an EINER Stelle ergaenzen kann, wenn sich die Rechtslage aendert.
 */
export const NACHWEISE_GRUNDPFLICHT: NachweisCode[] = [
    'gefaehrdungsbeurteilung', 'unterweisung', 'ersthelfer',
];

// ═══════════════════════════════════════════════════════════════════════════
// VORLAGEN
// ═══════════════════════════════════════════════════════════════════════════

export interface VorlageTaetigkeit {
    beschreibung: string;
    arbeitsmittel: string[];
    gefahrstoffe: string[];
}

export interface GewerkVorlage {
    id: GewerkId;
    /** ATV-Nummer der VOB/C bzw. das einschlaegige Regelwerk. Leerstring, wo es
     *  keine gibt — geraten wird nicht. */
    dinNummer: string;
    taetigkeiten: VorlageTaetigkeit[];
    /** Verweise in `GEFAEHRDUNG_BAUSTEINE`. */
    gefaehrdungen: GefaehrdungCode[];
    /** Verweise in `PSA_BAUSTEINE`. Die Grundausstattung wird ERGAENZT und
     *  gehoert nicht in diese Liste. */
    psa: PsaCode[];
    /** Verweise in `NACHWEIS_BAUSTEINE`. Die Grundpflicht wird ergaenzt. */
    nachweise: NachweisCode[];
    /**
     * Wortlaute aus `anhangII.ts`, die bei diesem Gewerk REGELMAESSIG
     * einschlaegig sind. Das ist eine Vorbelegung zum Ankreuzen und KEINE
     * Feststellung — die trifft der SiGeKo am Projekt.
     */
    anhangII: string[];
}

/**
 * `Record<GewerkId, …>` und nicht `GewerkVorlage[]`: der Compiler haelt damit
 * fest, dass die Liste vollstaendig ist. Ein Array liesse eine Luecke zu, die
 * erst auf der Baustelle als leere Seite auffiele.
 */
export const GEWERK_VORLAGEN: Record<GewerkId, GewerkVorlage> = {
    rohbau: {
        id: 'rohbau',
        dinNummer: 'ATV DIN 18330 / 18331',
        taetigkeiten: [
            { beschreibung: 'Schalungsarbeiten', arbeitsmittel: ['Rahmenschalung', 'Kran', 'Schalungsstützen'], gefahrstoffe: ['Schalöl'] },
            { beschreibung: 'Bewehrungsarbeiten', arbeitsmittel: ['Biegemaschine', 'Kran', 'Bindezange'], gefahrstoffe: [] },
            { beschreibung: 'Betonierarbeiten', arbeitsmittel: ['Betonpumpe', 'Innenrüttler', 'Kübel'], gefahrstoffe: ['Frischbeton (chromatarm)'] },
            { beschreibung: 'Maurerarbeiten', arbeitsmittel: ['Kran', 'Mischmaschine', 'Steinsäge'], gefahrstoffe: ['Zementmörtel', 'Quarzstaub'] },
            { beschreibung: 'Montage von Fertigteilen', arbeitsmittel: ['Kran', 'Montagestützen', 'Anschlagmittel'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['absturz_kante', 'absturz_oeffnung', 'kranlast', 'hautkontakt_zement', 'laerm', 'staub_quarz', 'handtransport'],
        psa: ['handschuhe_mech', 'handschuhe_chem', 'gehoerschutz', 'schutzbrille', 'chemikalienstiefel'],
        nachweise: ['statik_freigabe', 'kranpruefung', 'kranfuehrer', 'anschlaeger', 'geruestfreigabe', 'maschinen_pruefbuch'],
        anhangII: ['Auf-/Abbau von Massivbauelementen mit kraftbetriebenen Hebezeugen'],
    },
    erdbau: {
        id: 'erdbau',
        dinNummer: 'ATV DIN 18300',
        taetigkeiten: [
            { beschreibung: 'Baugrube und Gräben ausheben', arbeitsmittel: ['Bagger', 'Radlader', 'Kipper'], gefahrstoffe: [] },
            { beschreibung: 'Böschen, Verbauen und Sichern', arbeitsmittel: ['Grabenverbaugerät', 'Spundbohlen'], gefahrstoffe: [] },
            { beschreibung: 'Verfüllen und Verdichten', arbeitsmittel: ['Rüttelplatte', 'Walze'], gefahrstoffe: [] },
            { beschreibung: 'Leitungssuche und Freilegen von Leitungen', arbeitsmittel: ['Leitungssuchgerät', 'Saugbagger'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['verschuettung', 'verkehr', 'absturz_kante', 'laerm', 'vibration', 'stromschlag'],
        psa: ['warnkleidung', 'gehoerschutz', 'handschuhe_mech'],
        nachweise: ['maschinen_pruefbuch', 'betriebsanweisung', 'entsorgungsnachweis'],
        anhangII: ['Verschüttungsgefahr > 5m (Baugruben/Gräben)'],
    },
    geruestbau: {
        id: 'geruestbau',
        dinNummer: 'ATV DIN 18451',
        taetigkeiten: [
            { beschreibung: 'Gerüst auf-, um- und abbauen', arbeitsmittel: ['Gerüstbauteile', 'Bauaufzug', 'Kran'], gefahrstoffe: [] },
            { beschreibung: 'Verankern und Aussteifen', arbeitsmittel: ['Bohrhammer', 'Ankerhülsen'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Freigabe kennzeichnen und prüfen', arbeitsmittel: ['Prüfprotokoll', 'Kennzeichnungsschild'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['absturz_geruest', 'herabfallend', 'handtransport', 'staub_quarz'],
        psa: ['auffanggurt', 'handschuhe_mech', 'staubmaske_ffp2'],
        nachweise: ['geruestfreigabe', 'psaga_pruefung', 'leiter_geruest_pruefung', 'statik_freigabe'],
        anhangII: ['Absturzgefahr > 7m'],
    },
    zimmerer_dachdecker: {
        id: 'zimmerer_dachdecker',
        dinNummer: 'ATV DIN 18334 / 18338',
        taetigkeiten: [
            { beschreibung: 'Dachstuhl abbinden und aufrichten', arbeitsmittel: ['Kran', 'Kettensäge', 'Nagelpistole'], gefahrstoffe: ['Holzschutzmittel'] },
            { beschreibung: 'Lattung und Schalung', arbeitsmittel: ['Handkreissäge', 'Nagler'], gefahrstoffe: ['Holzstaub'] },
            { beschreibung: 'Dachabdichtung', arbeitsmittel: ['Gasbrenner', 'Heißluftgerät'], gefahrstoffe: ['Bitumen'] },
            { beschreibung: 'Dacheindeckung und Dämmung', arbeitsmittel: ['Dachdeckeraufzug', 'Handwerkzeug'], gefahrstoffe: ['Mineralwolle'] },
        ],
        gefaehrdungen: ['absturz_dach', 'herabfallend', 'brand_heissarbeit', 'maschine_schnitt', 'faserstaub'],
        psa: ['auffanggurt', 'handschuhe_mech', 'schnittschutzhose', 'staubmaske_ffp2', 'gehoerschutz'],
        nachweise: ['geruestfreigabe', 'psaga_pruefung', 'erlaubnisschein', 'maschinen_pruefbuch'],
        anhangII: ['Absturzgefahr > 7m'],
    },
    kran: {
        id: 'kran',
        dinNummer: 'BetrSichV / DGUV Vorschrift 52',
        taetigkeiten: [
            { beschreibung: 'Kran aufbauen, umsetzen und abbauen', arbeitsmittel: ['Mobilkran', 'Ballast', 'Fundament'], gefahrstoffe: [] },
            { beschreibung: 'Lasten anschlagen und heben', arbeitsmittel: ['Anschlagmittel', 'Lastaufnahmemittel'], gefahrstoffe: [] },
            { beschreibung: 'Tägliche Prüfung und Windsicherung', arbeitsmittel: ['Prüfbuch', 'Anemometer'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['kranlast', 'herabfallend', 'absturz_kante', 'stromschlag'],
        psa: ['warnkleidung', 'handschuhe_mech', 'auffanggurt'],
        nachweise: ['kranpruefung', 'kranfuehrer', 'anschlaeger', 'statik_freigabe'],
        anhangII: ['Auf-/Abbau von Massivbauelementen mit kraftbetriebenen Hebezeugen', 'Arbeiten in < 5m Abstand von Hochspannungsleitungen'],
    },
    elektro: {
        id: 'elektro',
        dinNummer: 'ATV DIN 18382',
        taetigkeiten: [
            { beschreibung: 'Leitungen verlegen', arbeitsmittel: ['Kabeltrommel', 'Kabelzugmaschine'], gefahrstoffe: [] },
            { beschreibung: 'Schlitze und Durchbrüche herstellen', arbeitsmittel: ['Schlitzfräse', 'Bohrhammer'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Schaltschränke und Verteiler installieren', arbeitsmittel: ['Handwerkzeug', 'Drehmomentschlüssel'], gefahrstoffe: [] },
            { beschreibung: 'Prüfen und in Betrieb nehmen', arbeitsmittel: ['Installationstester', 'Messgeräte'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['stromschlag', 'baustrom', 'staub_quarz', 'stolpern', 'zwangshaltung'],
        psa: ['isolierhandschuhe', 'gesichtsschutz', 'staubmaske_ffp2', 'gehoerschutz'],
        nachweise: ['elektrofachkraft', 'elektro_pruefung', 'inbetriebnahme', 'betriebsanweisung'],
        anhangII: [],
    },
    shk: {
        id: 'shk',
        dinNummer: 'ATV DIN 18380 / 18381',
        taetigkeiten: [
            { beschreibung: 'Rohrleitungen für Trink- und Abwasser installieren', arbeitsmittel: ['Rohrschneider', 'Pressmaschine'], gefahrstoffe: ['Lötzinn', 'Flussmittel'] },
            { beschreibung: 'Heizungs- und Lüftungsleitungen montieren', arbeitsmittel: ['Schweißgerät', 'Kernbohrgerät'], gefahrstoffe: ['Schweißrauche'] },
            { beschreibung: 'Sanitärobjekte und Armaturen setzen', arbeitsmittel: ['Handwerkzeug', 'Bohrmaschine'], gefahrstoffe: ['Silikon'] },
            { beschreibung: 'Druck- und Dichtheitsprüfung', arbeitsmittel: ['Druckprüfpumpe'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['verbruehung', 'schweissrauch', 'brand_heissarbeit', 'sturz_glatt', 'zwangshaltung'],
        psa: ['schweisserschild', 'handschuhe_mech', 'schutzbrille', 'knieschutz'],
        nachweise: ['schweisserpruefung', 'erlaubnisschein', 'druckpruefung', 'inbetriebnahme'],
        anhangII: [],
    },
    estrich: {
        id: 'estrich',
        dinNummer: 'ATV DIN 18353',
        taetigkeiten: [
            { beschreibung: 'Untergrund vorbereiten und Dämmung verlegen', arbeitsmittel: ['Handwerkzeug'], gefahrstoffe: ['Mineralwolle'] },
            { beschreibung: 'Estrich fördern und einbauen', arbeitsmittel: ['Estrichpumpe', 'Förderschlauch', 'Glättmaschine'], gefahrstoffe: ['Zementestrich', 'Calciumsulfatestrich'] },
            { beschreibung: 'Schleifen und Nachbehandeln', arbeitsmittel: ['Schleifmaschine mit Absaugung'], gefahrstoffe: ['Quarzstaub'] },
        ],
        gefaehrdungen: ['hautkontakt_zement', 'zwangshaltung', 'staub_quarz', 'laerm', 'handtransport'],
        psa: ['chemikalienstiefel', 'handschuhe_chem', 'knieschutz', 'staubmaske_ffp2'],
        nachweise: ['betriebsanweisung', 'sicherheitsdatenblaetter', 'maschinen_pruefbuch'],
        anhangII: [],
    },
    trockenbau: {
        id: 'trockenbau',
        dinNummer: 'ATV DIN 18340',
        taetigkeiten: [
            { beschreibung: 'Unterkonstruktion stellen', arbeitsmittel: ['Bohrhammer', 'Blechschere', 'Fahrgerüst'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Beplanken und Dämmen', arbeitsmittel: ['Akkuschrauber', 'Plattenheber'], gefahrstoffe: ['Mineralwolle'] },
            { beschreibung: 'Spachteln und Schleifen', arbeitsmittel: ['Schleifgerät mit Absaugung'], gefahrstoffe: ['Gipsstaub'] },
        ],
        gefaehrdungen: ['absturz_kante', 'staub_quarz', 'faserstaub', 'maschine_schnitt', 'zwangshaltung'],
        psa: ['staubmaske_ffp2', 'handschuhe_mech', 'schutzbrille', 'knieschutz'],
        nachweise: ['leiter_geruest_pruefung', 'betriebsanweisung'],
        anhangII: [],
    },
    maler: {
        id: 'maler',
        dinNummer: 'ATV DIN 18363 / 18365',
        taetigkeiten: [
            { beschreibung: 'Untergrund vorbereiten und schleifen', arbeitsmittel: ['Schleifgerät mit Absaugung'], gefahrstoffe: ['Altbeschichtungen', 'Quarzstaub'] },
            { beschreibung: 'Beschichten und Lackieren', arbeitsmittel: ['Rolle', 'Airless-Gerät', 'Fahrgerüst'], gefahrstoffe: ['Lösemittelhaltige Lacke', 'Isocyanate'] },
            { beschreibung: 'Bodenbeläge verlegen', arbeitsmittel: ['Klebespachtel', 'Andrückwalze'], gefahrstoffe: ['Klebstoffe'] },
        ],
        gefaehrdungen: ['loesemittel', 'absturz_kante', 'zwangshaltung', 'staub_quarz'],
        psa: ['handschuhe_chem', 'schutzbrille', 'staubmaske_ffp2', 'knieschutz'],
        nachweise: ['sicherheitsdatenblaetter', 'betriebsanweisung', 'leiter_geruest_pruefung', 'vorsorge'],
        anhangII: [],
    },
    fliesenleger: {
        id: 'fliesenleger',
        dinNummer: 'ATV DIN 18352',
        taetigkeiten: [
            { beschreibung: 'Untergrund prüfen und grundieren', arbeitsmittel: ['Handwerkzeug'], gefahrstoffe: ['Grundierung'] },
            { beschreibung: 'Fliesen zuschneiden', arbeitsmittel: ['Nassschneidetisch', 'Winkelschleifer'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Verlegen und Verfugen', arbeitsmittel: ['Zahnkelle', 'Rührwerk'], gefahrstoffe: ['Zementkleber', 'Epoxidharzfuge'] },
        ],
        gefaehrdungen: ['zwangshaltung', 'hautkontakt_zement', 'staub_quarz', 'laerm'],
        psa: ['knieschutz', 'handschuhe_chem', 'schutzbrille', 'gehoerschutz'],
        nachweise: ['betriebsanweisung', 'sicherheitsdatenblaetter'],
        anhangII: [],
    },
    fassade: {
        id: 'fassade',
        dinNummer: 'ATV DIN 18351 / 18360 / 18361',
        taetigkeiten: [
            { beschreibung: 'Unterkonstruktion montieren', arbeitsmittel: ['Gerüst', 'Bohrhammer', 'Setzgerät'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Fassadenelemente versetzen', arbeitsmittel: ['Kran', 'Vakuumheber', 'Hubarbeitsbühne'], gefahrstoffe: [] },
            { beschreibung: 'Fenster und Verglasung einbauen', arbeitsmittel: ['Saugheber', 'Montagekran'], gefahrstoffe: ['PU-Schaum'] },
            { beschreibung: 'Abdichten und Verfugen', arbeitsmittel: ['Kartuschenpresse'], gefahrstoffe: ['Dichtstoffe'] },
        ],
        gefaehrdungen: ['absturz_geruest', 'herabfallend', 'kranlast', 'loesemittel', 'handtransport'],
        psa: ['auffanggurt', 'handschuhe_schnitt', 'schutzbrille'],
        nachweise: ['geruestfreigabe', 'psaga_pruefung', 'kranpruefung', 'anschlaeger'],
        anhangII: ['Absturzgefahr > 7m'],
    },
    aufzugsbau: {
        id: 'aufzugsbau',
        dinNummer: 'ATV DIN 18385',
        taetigkeiten: [
            { beschreibung: 'Schacht vermessen und Führungsschienen montieren', arbeitsmittel: ['Montagebühne', 'Bohrhammer'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Antrieb, Kabine und Gegengewicht einbauen', arbeitsmittel: ['Kettenzug', 'Hebezeug'], gefahrstoffe: ['Öle und Fette'] },
            { beschreibung: 'Steuerung anschließen und prüfen', arbeitsmittel: ['Messgeräte'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['absturz_oeffnung', 'enge_raeume', 'stromschlag', 'herabfallend', 'kranlast'],
        psa: ['auffanggurt', 'isolierhandschuhe', 'handschuhe_mech'],
        nachweise: ['psaga_pruefung', 'elektrofachkraft', 'inbetriebnahme', 'maschinen_pruefbuch'],
        anhangII: [],
    },
    abbruch: {
        id: 'abbruch',
        dinNummer: 'ATV DIN 18459',
        taetigkeiten: [
            { beschreibung: 'Entkernen und Rückbau von Ausbauteilen', arbeitsmittel: ['Handwerkzeug', 'Mini-Bagger'], gefahrstoffe: ['Altlasten', 'Staub'] },
            { beschreibung: 'Abbruch tragender Bauteile', arbeitsmittel: ['Abbruchbagger', 'Abbruchzange', 'Stemmhammer'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Sortieren, Laden und Entsorgen', arbeitsmittel: ['Radlader', 'Container'], gefahrstoffe: ['Gefährliche Abfälle'] },
        ],
        gefaehrdungen: ['einsturz', 'herabfallend', 'staub_quarz', 'laerm', 'vibration', 'verkehr'],
        psa: ['staubmaske_ffp2', 'gehoerschutz', 'schutzbrille', 'warnkleidung', 'handschuhe_mech'],
        nachweise: ['ausbau_abbruchanweisung', 'statik_freigabe', 'entsorgungsnachweis', 'maschinen_pruefbuch'],
        anhangII: [],
    },
    schadstoff: {
        id: 'schadstoff',
        dinNummer: 'TRGS 519 / TRGS 521 / TRGS 524',
        taetigkeiten: [
            { beschreibung: 'Bereich abschotten und Schwarz-Weiß-Anlage stellen', arbeitsmittel: ['Folienabschottung', 'Unterdruckhaltung'], gefahrstoffe: [] },
            { beschreibung: 'Schadstoffhaltige Bauteile ausbauen', arbeitsmittel: ['Emissionsarme Verfahren', 'Industriesauger H'], gefahrstoffe: ['Asbest', 'KMF', 'PAK', 'PCB'] },
            { beschreibung: 'Reinigen, freimessen und freigeben', arbeitsmittel: ['Messtechnik'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['faserstaub', 'enge_raeume', 'zwangshaltung'],
        psa: ['atemschutz_p3', 'vollschutz', 'handschuhe_chem'],
        nachweise: ['sachkunde_trgs519', 'freimessung', 'entsorgungsnachweis', 'vorsorge', 'betriebsanweisung'],
        anhangII: ['Gefahrstoffe Kategorie 1A/1B (karzinogen, mutagen, reproduktionstoxisch)'],
    },
    strassenbau: {
        id: 'strassenbau',
        dinNummer: 'ATV DIN 18315 / 18316 / 18317',
        taetigkeiten: [
            { beschreibung: 'Verkehrssicherung stellen und unterhalten', arbeitsmittel: ['Absperrmaterial', 'Lichtsignalanlage'], gefahrstoffe: [] },
            { beschreibung: 'Frässcheiden und Aufbrechen', arbeitsmittel: ['Straßenfräse', 'Bagger'], gefahrstoffe: ['Quarzstaub', 'PAK-haltiger Asphalt'] },
            { beschreibung: 'Asphalt einbauen und verdichten', arbeitsmittel: ['Fertiger', 'Walze'], gefahrstoffe: ['Bitumendämpfe'] },
        ],
        gefaehrdungen: ['verkehr', 'laerm', 'staub_quarz', 'vibration', 'handtransport'],
        psa: ['warnkleidung', 'gehoerschutz', 'handschuhe_mech', 'schutzbrille'],
        nachweise: ['verkehrsrechtliche_anordnung', 'maschinen_pruefbuch', 'entsorgungsnachweis'],
        anhangII: [],
    },
    galabau: {
        id: 'galabau',
        dinNummer: 'ATV DIN 18320',
        taetigkeiten: [
            { beschreibung: 'Erdmodellierung und Pflanzarbeiten', arbeitsmittel: ['Bagger', 'Handwerkzeug'], gefahrstoffe: ['Dünger'] },
            { beschreibung: 'Baumpflege und Fällarbeiten', arbeitsmittel: ['Motorsäge', 'Hubarbeitsbühne', 'Häcksler'], gefahrstoffe: ['Kraftstoff'] },
            { beschreibung: 'Wege und Beläge herstellen', arbeitsmittel: ['Rüttelplatte', 'Steinschneider'], gefahrstoffe: ['Quarzstaub'] },
        ],
        gefaehrdungen: ['maschine_schnitt', 'herabfallend', 'laerm', 'vibration', 'zwangshaltung'],
        psa: ['schnittschutzhose', 'gesichtsschutz', 'gehoerschutz', 'handschuhe_mech'],
        nachweise: ['maschinen_pruefbuch', 'betriebsanweisung', 'vorsorge'],
        anhangII: [],
    },
    reinigung: {
        id: 'reinigung',
        dinNummer: 'ATV DIN 18299 (keine eigene ATV)',
        taetigkeiten: [
            { beschreibung: 'Grob- und Bauschlussreinigung', arbeitsmittel: ['Industriesauger', 'Reinigungsmaschine'], gefahrstoffe: ['Reinigungsmittel'] },
            { beschreibung: 'Glas- und Fassadenreinigung', arbeitsmittel: ['Hubarbeitsbühne', 'Teleskopstange'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['sturz_glatt', 'absturz_kante', 'loesemittel', 'zwangshaltung'],
        psa: ['handschuhe_chem', 'schutzbrille'],
        nachweise: ['sicherheitsdatenblaetter', 'betriebsanweisung', 'leiter_geruest_pruefung'],
        anhangII: [],
    },
    tischler: {
        id: 'tischler',
        dinNummer: 'ATV DIN 18355',
        taetigkeiten: [
            { beschreibung: 'Türen, Zargen und Einbauten montieren', arbeitsmittel: ['Handkreissäge', 'Akkuschrauber'], gefahrstoffe: ['Holzstaub'] },
            { beschreibung: 'Zuschnitt auf der Baustelle', arbeitsmittel: ['Tischkreissäge mit Absaugung'], gefahrstoffe: ['Holzstaub (Buche/Eiche)'] },
            { beschreibung: 'Oberflächen behandeln', arbeitsmittel: ['Schleifgerät', 'Sprühgerät'], gefahrstoffe: ['Lacke', 'Beizen'] },
        ],
        gefaehrdungen: ['maschine_schnitt', 'staub_quarz', 'loesemittel', 'handtransport'],
        psa: ['staubmaske_ffp2', 'gehoerschutz', 'schutzbrille', 'handschuhe_mech'],
        nachweise: ['maschinen_pruefbuch', 'betriebsanweisung', 'vorsorge'],
        anhangII: [],
    },
    stuckateur: {
        id: 'stuckateur',
        dinNummer: 'ATV DIN 18345 / 18350',
        taetigkeiten: [
            { beschreibung: 'Dämmplatten kleben und dübeln', arbeitsmittel: ['Gerüst', 'Bohrhammer'], gefahrstoffe: ['Klebemörtel'] },
            { beschreibung: 'Putz auftragen und abziehen', arbeitsmittel: ['Putzmaschine', 'Kartätsche'], gefahrstoffe: ['Kalk-Zement-Putz'] },
            { beschreibung: 'Armierung und Oberputz', arbeitsmittel: ['Handwerkzeug', 'Rührwerk'], gefahrstoffe: ['Silikatputz'] },
        ],
        gefaehrdungen: ['absturz_geruest', 'hautkontakt_zement', 'zwangshaltung', 'staub_quarz'],
        psa: ['handschuhe_chem', 'schutzbrille', 'staubmaske_ffp2'],
        nachweise: ['geruestfreigabe', 'sicherheitsdatenblaetter', 'betriebsanweisung'],
        anhangII: [],
    },
    dachklempner: {
        id: 'dachklempner',
        dinNummer: 'ATV DIN 18339',
        taetigkeiten: [
            { beschreibung: 'Bleche zuschneiden und kanten', arbeitsmittel: ['Blechschere', 'Abkantbank'], gefahrstoffe: [] },
            { beschreibung: 'Attika, Rinnen und Anschlüsse einbauen', arbeitsmittel: ['Lötkolben', 'Heißluftgerät'], gefahrstoffe: ['Lötzinn', 'Flussmittel'] },
            { beschreibung: 'Fallrohre und Entwässerung montieren', arbeitsmittel: ['Handwerkzeug', 'Hubarbeitsbühne'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['absturz_dach', 'herabfallend', 'brand_heissarbeit', 'maschine_schnitt'],
        psa: ['auffanggurt', 'handschuhe_schnitt', 'schutzbrille'],
        nachweise: ['psaga_pruefung', 'erlaubnisschein', 'geruestfreigabe'],
        anhangII: ['Absturzgefahr > 7m'],
    },
    sprinkler: {
        id: 'sprinkler',
        dinNummer: 'VdS CEA 4001 / DIN 14462',
        taetigkeiten: [
            { beschreibung: 'Rohrnetz montieren und aufhängen', arbeitsmittel: ['Hubarbeitsbühne', 'Rohrpresse', 'Kernbohrer'], gefahrstoffe: ['Quarzstaub'] },
            { beschreibung: 'Sprinkler und Ventilstationen setzen', arbeitsmittel: ['Handwerkzeug'], gefahrstoffe: [] },
            { beschreibung: 'Dichtheits- und Wirksamkeitsprüfung', arbeitsmittel: ['Druckprüfpumpe', 'Messtechnik'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['absturz_kante', 'brand_heissarbeit', 'sturz_glatt', 'zwangshaltung', 'herabfallend'],
        psa: ['auffanggurt', 'handschuhe_mech', 'schutzbrille'],
        nachweise: ['druckpruefung', 'erlaubnisschein', 'inbetriebnahme', 'schweisserpruefung'],
        anhangII: [],
    },
    spezialtiefbau: {
        id: 'spezialtiefbau',
        dinNummer: 'ATV DIN 18301 / 18303 / 18304',
        taetigkeiten: [
            { beschreibung: 'Bohrpfähle und Verbauwände herstellen', arbeitsmittel: ['Drehbohrgerät', 'Ramme'], gefahrstoffe: ['Bentonit'] },
            { beschreibung: 'Anker setzen und vorspannen', arbeitsmittel: ['Ankerbohrgerät', 'Spannpresse'], gefahrstoffe: ['Zementsuspension'] },
            { beschreibung: 'Beweissicherung und Messung', arbeitsmittel: ['Messtechnik', 'Inklinometer'], gefahrstoffe: [] },
        ],
        gefaehrdungen: ['verschuettung', 'einsturz', 'laerm', 'vibration', 'herabfallend', 'ertrinken'],
        psa: ['warnkleidung', 'gehoerschutz', 'handschuhe_mech', 'schutzbrille'],
        nachweise: ['statik_freigabe', 'maschinen_pruefbuch', 'betriebsanweisung'],
        anhangII: ['Verschüttungsgefahr > 5m (Baugruben/Gräben)', 'Brunnenbau, unterirdische Erdarbeiten, Tunnelbau'],
    },
    sonstiges: {
        id: 'sonstiges',
        dinNummer: '',
        taetigkeiten: [],
        gefaehrdungen: ['stolpern', 'handtransport'],
        psa: [],
        nachweise: [],
        anhangII: [],
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// ZUGRIFF
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vorlage zu einer Katalog-id.
 *
 * Nimmt `string | null` und nicht `GewerkId`, weil der Aufrufer das Ergebnis
 * der Freitext-Zuordnung durchreicht — und die kann „unbekannt" lauten. In dem
 * Fall gibt es KEINE Vorlage: eine falsche Vorlage waere schlimmer als keine,
 * weil sie Gefaehrdungen behauptet, die es nicht gibt, und die verschweigt,
 * die es gibt.
 */
export const vorlageFuer = (katalogId: string | null | undefined): GewerkVorlage | null => {
    if (!katalogId) return null;
    return (GEWERK_VORLAGEN as Record<string, GewerkVorlage | undefined>)[katalogId] ?? null;
};

/** Grundausstattung plus gewerkspezifische PSA, ohne Dubletten und in
 *  stabiler Reihenfolge. */
export const psaFuerVorlage = (vorlage: GewerkVorlage | null): PsaEintrag[] => {
    const codes = [...PSA_GRUNDAUSSTATTUNG, ...(vorlage?.psa ?? [])];
    const gesehen = new Set<PsaCode>();
    return codes
        .filter(c => (gesehen.has(c) ? false : (gesehen.add(c), true)))
        .map(c => ({ ...PSA_BAUSTEINE[c] }));
};

/** Die typischen Eigengefaehrdungen der Vorlage, als Kopien — der Aufrufer
 *  darf sie bearbeiten, ohne den Katalog zu veraendern. */
export const gefaehrdungenFuerVorlage = (vorlage: GewerkVorlage | null): VorlageGefaehrdung[] =>
    (vorlage?.gefaehrdungen ?? []).map(c => ({ ...GEFAEHRDUNG_BAUSTEINE[c] }));

/** Grundpflicht plus gewerkspezifische Nachweise, ohne Dubletten. */
export const nachweiseFuerVorlage = (vorlage: GewerkVorlage | null): string[] => {
    const codes = [...NACHWEISE_GRUNDPFLICHT, ...(vorlage?.nachweise ?? [])];
    const gesehen = new Set<NachweisCode>();
    return codes
        .filter(c => (gesehen.has(c) ? false : (gesehen.add(c), true)))
        .map(c => NACHWEIS_BAUSTEINE[c]);
};

/** Anzahl der gefuehrten Vorlagen — fuer die Anzeige „Katalog Fassung x,
 *  n Gewerke" und fuer den Test, der die Vollstaendigkeit festhaelt. */
export const VORLAGEN_ANZAHL = Object.keys(GEWERK_VORLAGEN).length;
